import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarIcon, FileText, Printer, Database, Save, Eye, Mail, Download, Trash2, FileDown } from "lucide-react";
import { format } from "date-fns";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateWordDocument } from "@/utils/wordDocumentGenerator";
import { useSharedData } from "./AgreementsPage";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  property_address: string;
  current_balance: number;
  payment_amount: number;
  interest_rate: number;
  first_payment_date: string;
  date_of_sale: string;
  created_at: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
}

export function AgreementGenerator() {
  const { agreementData, setAgreementData } = useSharedData();

  const [showPreview, setShowPreview] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [savedAgreements, setSavedAgreements] = useState<any[]>([]);

  // Currency formatting helper functions
  const formatCurrencyInput = (value: string) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    // Convert to number and format with commas
    const number = parseFloat(numericValue);
    if (isNaN(number)) return '';
    return number.toLocaleString('en-US');
  };

  const unformatCurrency = (formattedValue: string) => {
    // Remove commas and return the raw numeric string
    return formattedValue.replace(/,/g, '');
  };

  const handleCurrencyInputChange = (field: string, formattedValue: string) => {
    const numericValue = unformatCurrency(formattedValue);
    setAgreementData(prev => ({
      ...prev,
      [field]: numericValue
    }));
  };
  const { toast } = useToast();

  // Financial breakdown calculations
  const calculateFinancialBreakdown = (selectedDeal?: Deal) => {
    if (!selectedDeal) return null;

    // Use manual override if provided, otherwise try database fields
    const downPayment = agreementData.originalDownPayment ? 
      parseFloat(agreementData.originalDownPayment) : 
      (selectedDeal.down_payment_amount || selectedDeal.beginning_balance || 0);
    const monthlyPayment = selectedDeal.payment_amount || 0;
    const salesDate = selectedDeal.date_of_sale || selectedDeal.created_at;
    const firstPaymentDate = agreementData.firstPaymentDate || selectedDeal.first_payment_date;
    
    // Calculate months since sale to today
    const monthsSinceSale = salesDate ? 
      Math.max(0, Math.floor((new Date().getTime() - new Date(salesDate).getTime()) / (30.44 * 24 * 60 * 60 * 1000))) : 0;
    
    // Calculate months until first payment to buyer
    const monthsUntilFirstPayment = firstPaymentDate ? 
      Math.max(0, Math.floor((new Date(firstPaymentDate).getTime() - new Date().getTime()) / (30.44 * 24 * 60 * 60 * 1000))) : 0;

    const totalMonthlyPaymentsReceived = monthsSinceSale * monthlyPayment;
    const additionalPaymentsBeforeFirst = monthsUntilFirstPayment * monthlyPayment;
    const cashOfferAmount = parseFloat(agreementData.brokerOfferToSeller || '0');
    
    const totalCashFlow = downPayment + totalMonthlyPaymentsReceived + additionalPaymentsBeforeFirst + cashOfferAmount;

    return {
      downPayment,
      monthlyPayment,
      monthsSinceSale,
      totalMonthlyPaymentsReceived,
      monthsUntilFirstPayment,
      additionalPaymentsBeforeFirst,
      cashOfferAmount,
      totalCashFlow,
      salesDate
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const generateFinancialBreakdownText = () => {
    const selectedDeal = deals.find(deal => deal.id === selectedDealId);
    const breakdown = calculateFinancialBreakdown(selectedDeal);
    
    if (!breakdown || !selectedDeal) return '';

    return `FINANCIAL BREAKDOWN - TOTAL CASH RECEIVED

Property: ${agreementData.propertyAddress || selectedDeal.property_address}
Seller: ${agreementData.primarySellerName || selectedDeal.seller_name}

CASH FLOW SUMMARY:
• Initial Down Payment: ${formatCurrency(breakdown.downPayment)}
• Monthly Payments Received (${breakdown.monthsSinceSale} payments): ${formatCurrency(breakdown.totalMonthlyPaymentsReceived)}
• Additional Payments Before Transfer (${breakdown.monthsUntilFirstPayment} payments): ${formatCurrency(breakdown.additionalPaymentsBeforeFirst)}
• Cash Offer from Buyer: ${formatCurrency(breakdown.cashOfferAmount)}

TOTAL CASH RECEIVED: ${formatCurrency(breakdown.totalCashFlow)}

Monthly Payment Amount: ${formatCurrency(breakdown.monthlyPayment)}
Sale Date: ${breakdown.salesDate ? new Date(breakdown.salesDate).toLocaleDateString() : 'N/A'}
First Payment to New Buyer: ${agreementData.firstPaymentDate || 'N/A'}

This represents the total amount of money you will have received from this investment throughout its entire lifecycle, including your original down payment, all monthly payments collected to date, payments you'll receive before the transfer, and the final cash settlement.`;
  };
  
  // Fetch deals from database
  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const { data, error } = await supabase
          .from('deals')
          .select('id, deal_title, seller_name, property_address, current_balance, payment_amount, interest_rate, first_payment_date, date_of_sale, created_at, sales_price, down_payment_amount, beginning_balance')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        if (data) setDeals(data);
      } catch (error) {
        console.error('Error fetching deals:', error);
      }
    };
    
    fetchDeals();
    fetchSavedAgreements();
  }, []);

  // Fetch saved agreements from database
  const fetchSavedAgreements = async () => {
    try {
      const { data, error } = await supabase
        .from('agreements')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      if (data) setSavedAgreements(data);
    } catch (error) {
      console.error('Error fetching saved agreements:', error);
    }
  };

  const handleInputChange = (field: keyof typeof agreementData, value: any) => {
    setAgreementData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle deal selection and populate form
  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const selectedDeal = deals.find(deal => deal.id === dealId);
    
    if (selectedDeal) {
      // Format dates for display
      const formatDate = (dateStr: string) => {
        if (!dateStr) return "";
        try {
          const date = new Date(dateStr);
          // Check if date is valid
          if (isNaN(date.getTime())) {
            return dateStr;
          }
          return format(date, "MMMM dd, yyyy");
        } catch {
          return dateStr;
        }
      };
      
      // Calculate the actual first payment date (next month from deal's first payment date)
      const calculateFirstPaymentDate = (firstPaymentStr: string) => {
        if (!firstPaymentStr) return "";
        try {
          const originalDate = new Date(firstPaymentStr);
          if (isNaN(originalDate.getTime())) {
            return formatDate(firstPaymentStr);
          }
          // Get the next month's payment date
          const nextPaymentDate = new Date(originalDate);
          nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
          return format(nextPaymentDate, "MMMM dd, yyyy");
        } catch {
          return formatDate(firstPaymentStr);
        }
      };
      
      setAgreementData(prev => ({
        ...prev,
        primarySellerName: selectedDeal.seller_name || "",
        secondarySellerName: "",
        propertyAddress: selectedDeal.property_address || "",
        balance: selectedDeal.current_balance ? selectedDeal.current_balance.toString() : "",
        firstPaymentDate: calculateFirstPaymentDate(selectedDeal.first_payment_date),
        dateOfBalance: formatDate(selectedDeal.created_at),
        date: new Date(), // Set to today for the agreement
      }));
    }
  };

  const generatePartialAgreement = () => {
    const sellerNames = agreementData.secondarySellerName 
      ? `${agreementData.primarySellerName || "________________"} and ${agreementData.secondarySellerName}`
      : agreementData.primarySellerName || "________________";
    
    return `Commitment to Purchase Agreement

This Purchase Agreement made and entered into ${agreementData.date ? format(agreementData.date, "MMMM dd, yyyy") : "________________"},
By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.
The property address is: ${agreementData.propertyAddress || "________________"}
SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:

1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.
Purchaser agrees to buy the next $${agreementData.partialDollarAmount || "________________"} due and you agree to sell the above described sum for $${agreementData.brokerOfferToSeller || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"} and ending with the payment due ${agreementData.partialEndDate || "________________"} assuming there are no unpaid fees or costs accumulated by the borrowers.

2. COSTS: PURCHASER will pay title and appraisal costs

3. SELLER warrants that all real property taxes that are due and payable have been paid.

4. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:
A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.
B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.

5. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.

6. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.

7. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.


__________________________________    __________________________________________
${agreementData.primarySellerName || "Primary Seller"}                            Contract Funding Group, LLC

${agreementData.secondarySellerName ? `__________________________________\n${agreementData.secondarySellerName}` : ""}`;
  };

  const generateFullAgreement = () => {
    const sellerNames = agreementData.secondarySellerName 
      ? `${agreementData.primarySellerName || "________________"} and ${agreementData.secondarySellerName}`
      : agreementData.primarySellerName || "________________";
    
    return `Commitment to Purchase Agreement

This Purchase Agreement made and entered into ${agreementData.date ? format(agreementData.date, "MMMM dd, yyyy") : "________________"},
By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.
The property address is: ${agreementData.propertyAddress || "________________"}
SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:

1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.

2. PURCHASER agrees to buy the entire remaining balance of $${agreementData.balance || "________________"} and shall pay to SELLER $${agreementData.brokerOfferToSeller || "________________"} for the full purchase of said ${agreementData.paperType || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"}

3. COSTS: PURCHASER will pay title and appraisal costs

4. SELLER warrants that all real property taxes that are due and payable have been paid.

5. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:
A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.
B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.

6. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.

7. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.

8. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.


__________________________________    __________________________________________
${agreementData.primarySellerName || "Primary Seller"}                            Contract Funding Group, LLC

${agreementData.secondarySellerName ? `__________________________________\n${agreementData.secondarySellerName}` : ""}`;
  };

  const handlePrintPackage = () => {
    const agreementContent = agreementData.agreementType === "partial" 
      ? generatePartialAgreement() 
      : generateFullAgreement();
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Purchase Agreement Package - ${agreementData.primarySellerName}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.6; 
                margin: 0; 
                font-size: 12px;
              }
               .page {
                page-break-after: always;
                max-height: calc(100vh - 80px);
                padding: 30px 40px;
                box-sizing: border-box;
                overflow: hidden;
              }
              .page:last-child {
                page-break-after: auto;
              }
              .logo-header {
                text-align: center;
                margin-bottom: 30px;
              }
              .logo-img {
                max-width: 400px;
                height: auto;
                margin-bottom: 20px;
              }
              .agreement-content { 
                white-space: pre-wrap; 
                font-family: Arial, sans-serif; 
              }
              .signature-section {
                margin-top: 40px;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                width: 100%;
                flex-wrap: wrap;
              }
              .signature-line {
                border-bottom: 1px solid #000;
                width: 300px;
                height: 20px;
                display: inline-block;
                margin-bottom: 20px;
              }
              .signature-label {
                text-align: center;
                margin-top: 5px;
                font-size: 10px;
              }
              .signature-box {
                margin: 10px;
                min-width: 300px;
              }
              @media print {
                body { margin: 0; }
                .page {
                  page-break-after: always;
                  max-height: calc(100vh - 60px);
                  padding: 20px 30px;
                  overflow: hidden;
                }
                .page:last-child {
                  page-break-after: auto;
                }
              }
            </style>
          </head>
          <body>
            <!-- Cover Letter Page -->
            <div class="page">
              <div class="logo-header">
                <img src="/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" alt="Contract Funding Group Logo" class="logo-img" />
                <div style="font-size: 18px; font-weight: bold; color: #8B4513; margin-bottom: 8px;">CONTRACT FUNDING GROUP, LLC</div>
                <div style="font-size: 14px; color: #FF6B35; font-weight: 600;">Professional Note Purchasing Services</div>
              </div>
              
              <div style="text-align: right; margin-bottom: 30px; font-size: 11px;">
                ${agreementData.date ? format(agreementData.date, "MMMM dd, yyyy") : ""}
              </div>
              
              <div style="margin-bottom: 30px;">
                <div style="font-weight: bold;">${agreementData.primarySellerName}</div>
                <div style="font-size: 11px; color: #666;">Land Contract Holder</div>
                <div style="margin-top: 10px; font-weight: bold;">RE: Purchase Proposal for Land Contract</div>
                <div style="font-size: 11px; color: #666;">Property: ${agreementData.propertyAddress}</div>
              </div>
              
              <div style="margin-bottom: 20px;">
                <p>Dear ${agreementData.primarySellerName},</p>
                <p>Thank you for considering Contract Funding Group, LLC for the purchase of your land contract. We are pleased to present you with our formal purchase proposal for your review and consideration.</p>
              </div>
              
              <div style="background: #f9f9f9; padding: 15px; border-left: 4px solid #8B4513; margin: 20px 0;">
                <h3 style="color: #8B4513; margin-bottom: 10px;">Purchase Proposal Summary</h3>
                <div><strong>Property Address:</strong> ${agreementData.propertyAddress}</div>
                <div><strong>Current Balance:</strong> $${parseFloat(agreementData.balance || "0").toLocaleString()}</div>
                ${agreementData.agreementType === "partial" && agreementData.partialDollarAmount ? 
                  `<div><strong>Partial Purchase Amount:</strong> $${parseFloat(agreementData.partialDollarAmount).toLocaleString()}</div>` : ''
                }
                <div><strong>Our Offer:</strong> $${parseFloat(agreementData.brokerOfferToSeller || "0").toLocaleString()}</div>
                <div><strong>Purchase Type:</strong> ${agreementData.agreementType === "partial" ? "Partial Purchase" : "Full Purchase"}</div>
              </div>
              
              <div style="margin: 20px 0;">
                <h3 style="color: #8B4513; margin-bottom: 10px;">Our Process & What to Expect</h3>
                <div style="margin-bottom: 10px;">
                  <strong>1. Review & Acceptance:</strong> If you accept our proposal, we will begin the due diligence process immediately.
                </div>
                <div style="margin-bottom: 10px;">
                  <strong>2. Due Diligence (30 days):</strong> We will review the title, payment history, property value, and borrower information.
                </div>
                <div style="margin-bottom: 10px;">
                  <strong>3. Closing & Payment:</strong> Upon successful completion of due diligence, we will arrange closing and provide your payment.
                </div>
              </div>
              
              <div style="margin: 20px 0;">
                <h3 style="color: #8B4513; margin-bottom: 10px;">Why Choose Contract Funding Group?</h3>
                <ul style="margin-left: 20px;">
                  <li><strong>Fast Processing:</strong> We complete transactions typically within 30-45 days</li>
                  <li><strong>Fair Pricing:</strong> We offer competitive rates based on current market conditions</li>
                  <li><strong>Professional Service:</strong> Our experienced team handles all the paperwork</li>
                  <li><strong>No Fees to You:</strong> We pay all title and appraisal costs</li>
                </ul>
              </div>
              
              <p>The attached Purchase Agreement outlines the specific terms and conditions of our proposal. This offer is valid for 30 days from the date of this letter.</p>
              
              <div style="margin-top: 40px;">
                <p>Sincerely,</p>
                <div style="margin-top: 60px;">
                  <div style="border-bottom: 1px solid #000; width: 250px; margin-bottom: 5px;"></div>
                  <div style="font-size: 11px;">Contract Funding Group, LLC</div>
                  <div style="font-size: 10px; color: #666;">Authorized Representative</div>
                </div>
              </div>
              
              <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 10px; color: #666;">
                <div><strong>Contract Funding Group, LLC</strong></div>
                <div>Email: info@contractfundinggroup.com</div>
                <div>This proposal is confidential and proprietary.</div>
              </div>
            </div>
            
            <!-- Agreement Page -->
            <div class="page">
              <div class="logo-header">
                <img src="/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" alt="Contract Funding Group Logo" class="logo-img" />
              </div>
              <div class="agreement-content">${agreementContent.replace(/\n\n__________________________________.*[\s\S]*$/, '')}</div>
              <div class="signature-section">
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">${agreementData.primarySellerName || "Primary Seller"}</div>
                </div>
                ${agreementData.secondarySellerName ? `
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">${agreementData.secondarySellerName}</div>
                </div>` : ''}
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">Contract Funding Group, LLC</div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handlePrint = () => {
    const agreementContent = agreementData.agreementType === "partial" 
      ? generatePartialAgreement() 
      : generateFullAgreement();
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Purchase Agreement</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.6; 
                margin: 40px; 
                font-size: 12px;
              }
              .logo-header {
                text-align: center;
                margin-bottom: 30px;
              }
              .logo-img {
                max-width: 400px;
                height: auto;
                margin-bottom: 20px;
              }
              .agreement-content { 
                white-space: pre-wrap; 
                font-family: Arial, sans-serif; 
              }
              .signature-section {
                margin-top: 40px;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                width: 100%;
                flex-wrap: wrap;
              }
              .signature-line {
                border-bottom: 1px solid #000;
                width: 300px;
                height: 20px;
                display: inline-block;
                margin-bottom: 20px;
              }
              .signature-label {
                text-align: center;
                margin-top: 5px;
                font-size: 10px;
              }
              .signature-box {
                margin: 10px;
                min-width: 300px;
              }
            </style>
          </head>
          <body>
            <div class="logo-header">
              <img src="/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" alt="Contract Funding Group Logo" class="logo-img" />
            </div>
            <div class="agreement-content">${agreementContent.replace(/\n\n__________________________________.*[\s\S]*$/, '')}</div>
            <div class="signature-section">
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">${agreementData.primarySellerName || "Primary Seller"}</div>
              </div>
              ${agreementData.secondarySellerName ? `
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">${agreementData.secondarySellerName}</div>
              </div>` : ''}
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">Contract Funding Group, LLC</div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleSaveAgreement = async () => {
    if (!agreementData.date || !agreementData.primarySellerName || !agreementData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the date, primary seller name, and property address before saving.",
        variant: "destructive",
      });
      return;
    }

    try {
      const agreementContent = agreementData.agreementType === "partial" 
        ? generatePartialAgreement() 
        : generateFullAgreement();

      const sellerNames = agreementData.secondarySellerName 
        ? `${agreementData.primarySellerName} and ${agreementData.secondarySellerName}`
        : agreementData.primarySellerName;

      const agreementDataToSave = {
        agreement_type: agreementData.agreementType,
        agreement_date: agreementData.date ? format(agreementData.date, 'yyyy-MM-dd') : null,
        seller_name: sellerNames,
        seller_address: agreementData.brokerCompanyName, // Using broker as seller address for now
        buyer_name: agreementData.brokerCompanyName,
        buyer_address: "", // Could be added as a field later
        property_address: agreementData.propertyAddress,
        purchase_price: agreementData.balance ? parseFloat(agreementData.balance) : null,
        down_payment: null, // Could be added as a field later
        balance_due: agreementData.balance ? parseFloat(agreementData.balance) : null,
        interest_rate: null, // Could be added as a field later
        monthly_payment: null, // Could be added as a field later
        final_payment_date: agreementData.firstPaymentDate ? agreementData.firstPaymentDate : null,
        partial_purchase_amount: agreementData.partialDollarAmount ? parseFloat(agreementData.partialDollarAmount) : null,
        partial_monthly_payment: null, // Could be added as a field later
        partial_final_date: agreementData.partialEndDate ? agreementData.partialEndDate : null,
        agreement_content: agreementContent,
      };

      const { error } = await supabase
        .from('agreements')
        .insert([agreementDataToSave]);

      if (error) throw error;

      toast({
        title: "Agreement Saved",
        description: "The agreement has been successfully saved to the database.",
      });

      // Refresh the saved agreements list
      fetchSavedAgreements();
    } catch (error) {
      console.error('Error saving agreement:', error);
      toast({
        title: "Save Failed",
        description: "There was an error saving the agreement. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAgreement = async (agreementId: string, sellerName: string) => {
    try {
      const { error } = await supabase
        .from('agreements')
        .delete()
        .eq('id', agreementId);

      if (error) throw error;

      toast({
        title: "Agreement deleted",
        description: `Agreement for "${sellerName}" has been successfully deleted`,
      });

      fetchSavedAgreements(); // Refresh the list
    } catch (error) {
      console.error('Error deleting agreement:', error);
      toast({
        title: "Error deleting agreement",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const viewSavedAgreement = (agreement: any) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Saved Agreement - ${agreement.seller_name}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.6; 
                margin: 40px; 
                font-size: 12px;
              }
              .agreement-content { 
                white-space: pre-wrap; 
                font-family: Arial, sans-serif; 
              }
            </style>
          </head>
          <body>
            <div class="agreement-content">${agreement.agreement_content}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleEmailAgreement = async () => {
    if (!agreementData.recipientEmail) {
      toast({
        title: "Email Required",
        description: "Please enter an email address to send the agreement.",
        variant: "destructive",
      });
      return;
    }

    if (!agreementData.date || !agreementData.primarySellerName || !agreementData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the date, primary seller name, and property address before sending.",
        variant: "destructive",
      });
      return;
    }

    try {
      const agreementContent = agreementData.agreementType === "partial" 
        ? generatePartialAgreement() 
        : generateFullAgreement();

      const sellerNames = agreementData.secondarySellerName 
        ? `${agreementData.primarySellerName} and ${agreementData.secondarySellerName}`
        : agreementData.primarySellerName;

      // Create HTML version of the agreement with logo
      const htmlContent = `
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${window.location.origin}/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" 
               alt="Contract Funding Group Logo" style="max-width: 200px; height: auto;" />
        </div>
        <div style="font-family: Arial, sans-serif; line-height: 1.6; white-space: pre-wrap;">
          ${agreementContent.replace(/\n/g, '<br>')}
        </div>
      `;

      // Call edge function to send email
      const { error } = await supabase.functions.invoke('send-agreement-email', {
        body: {
          recipientEmail: agreementData.recipientEmail,
          recipientName: sellerNames,
          subject: `Purchase Agreement - ${agreementData.propertyAddress}`,
          htmlContent: htmlContent,
          agreementType: agreementData.agreementType
        }
      });

      if (error) throw error;

      toast({
        title: "Email Sent",
        description: `Agreement has been sent to ${agreementData.recipientEmail}`,
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Email Failed",
        description: "There was an error sending the email. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadWord = async () => {
    if (!agreementData.date || !agreementData.primarySellerName || !agreementData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the date, primary seller name, and property address before generating Word document.",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Generating Document",
        description: "Creating Word document...",
      });

      const blob = await generateWordDocument(agreementData);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Purchase_Agreement_${agreementData.primarySellerName.replace(/\s+/g, '_')}_${format(agreementData.date, 'yyyy-MM-dd')}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download Ready",
        description: "Word document has been generated and downloaded.",
      });
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast({
        title: "Generation Failed",
        description: "There was an error generating the Word document. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Purchase Agreement Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Load from Deals */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Load from Deals Database
            </Label>
            <Select value={selectedDealId} onValueChange={handleDealSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a deal to auto-populate form..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title} - {deal.seller_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Agreement Type Selection */}
          <div className="space-y-2">
            <Label>Agreement Type</Label>
            <Select 
              value={agreementData.agreementType} 
              onValueChange={(value: "partial" | "full") => handleInputChange("agreementType", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partial">Partial Purchase Agreement</SelectItem>
                <SelectItem value="full">Full Purchase Agreement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Agreement Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !agreementData.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {agreementData.date ? format(agreementData.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={agreementData.date}
                    onSelect={(date) => handleInputChange("date", date)}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Primary Seller Name</Label>
              <Input
                value={agreementData.primarySellerName}
                onChange={(e) => handleInputChange("primarySellerName", e.target.value)}
                placeholder="Enter primary seller name"
              />
            </div>

            <div className="space-y-2">
              <Label>Secondary Seller Name (Optional)</Label>
              <Input
                value={agreementData.secondarySellerName}
                onChange={(e) => handleInputChange("secondarySellerName", e.target.value)}
                placeholder="Enter secondary seller name (e.g., spouse)"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Address (for sending agreement)</Label>
              <Input
                type="email"
                value={agreementData.recipientEmail}
                onChange={(e) => handleInputChange("recipientEmail", e.target.value)}
                placeholder="Enter email address"
              />
            </div>

            <div className="space-y-2">
              <Label>Broker/Company Name</Label>
              <Input
                value={agreementData.brokerCompanyName}
                onChange={(e) => handleInputChange("brokerCompanyName", e.target.value)}
                placeholder="Contract Funding Group, LLC"
              />
            </div>

            <div className="space-y-2">
              <Label>Paper Type</Label>
              <Input
                value={agreementData.paperType}
                onChange={(e) => handleInputChange("paperType", e.target.value)}
                placeholder="e.g., land contract, promissory note"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Property Address</Label>
            <Textarea
              rows={2}
              value={agreementData.propertyAddress}
              onChange={(e) => handleInputChange("propertyAddress", e.target.value)}
              placeholder="Enter complete property address"
            />
          </div>

          {/* Purchase Details - Shared Fields */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Purchase Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Current Balance</Label>
                <Input
                  value={formatCurrencyInput(agreementData.balance)}
                  onChange={(e) => handleCurrencyInputChange("balance", e.target.value)}
                  placeholder="Enter current balance amount"
                />
              </div>

              <div className="space-y-2">
                <Label>Date of Balance</Label>
                <Input
                  value={agreementData.dateOfBalance}
                  onChange={(e) => handleInputChange("dateOfBalance", e.target.value)}
                  placeholder="Enter date of balance"
                />
              </div>

              <div className="space-y-2">
                <Label>Broker Offer to Seller</Label>
                <Input
                  value={formatCurrencyInput(agreementData.brokerOfferToSeller)}
                  onChange={(e) => handleCurrencyInputChange("brokerOfferToSeller", e.target.value)}
                  placeholder="Enter offer amount"
                />
              </div>

              <div className="space-y-2">
                <Label>First Payment Date</Label>
                <Input
                  value={agreementData.firstPaymentDate}
                  onChange={(e) => handleInputChange("firstPaymentDate", e.target.value)}
                  placeholder="Enter first payment date"
                />
              </div>

              <div className="space-y-2">
                <Label>Original Down Payment (if different from database)</Label>
                <Input
                  value={formatCurrencyInput(agreementData.originalDownPayment || '')}
                  onChange={(e) => handleCurrencyInputChange("originalDownPayment", e.target.value)}
                  placeholder="Enter actual down payment received"
                />
              </div>
            </div>
          </div>

          {/* Partial Agreement Specific Fields */}
          {agreementData.agreementType === "partial" && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Partial Purchase Specific Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Partial Dollar Amount</Label>
                  <Input
                    value={agreementData.partialDollarAmount || ""}
                    onChange={(e) => handleInputChange("partialDollarAmount", e.target.value)}
                    placeholder="Enter partial amount to purchase"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Partial End Date</Label>
                  <Input
                    value={agreementData.partialEndDate || ""}
                    onChange={(e) => handleInputChange("partialEndDate", e.target.value)}
                    placeholder="Enter end date for partial purchase"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Email-Ready Offer Letter */}
          {selectedDealId && (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email-Ready Offer Letter
                </CardTitle>
                <p className="text-sm text-green-700 mt-2">Click the text area below to automatically copy a professional offer letter for email</p>
              </CardHeader>
              <CardContent>
                {(() => {
                  const selectedDeal = deals.find(deal => deal.id === selectedDealId);
                  const breakdown = calculateFinancialBreakdown(selectedDeal);
                  
                  if (!breakdown || !selectedDeal) return <p>No deal selected or insufficient data</p>;

                  const generateOfferLetter = () => {
                    const currentDate = new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });

                    return `Subject: Exclusive Cash Offer for Your ${agreementData.paperType || 'Land Contract'} - ${agreementData.propertyAddress || selectedDeal.property_address}

Dear ${agreementData.primarySellerName},

I hope this message finds you well. I'm writing to present you with an exclusive cash offer for your ${agreementData.paperType || 'land contract'} on the property located at:

📍 ${agreementData.propertyAddress || selectedDeal.property_address}

💰 CASH OFFER: ${formatCurrency(parseFloat(agreementData.brokerOfferToSeller) || 0)}

Here's what makes this offer exceptional for you:

💵 TOTAL CASH YOU'VE ALREADY RECEIVED:
   • Initial Down Payment: ${formatCurrency(breakdown.downPayment)}
   • Monthly Payments Collected: ${formatCurrency(breakdown.totalMonthlyPaymentsReceived)} (${breakdown.monthsSinceSale} payments)
   • Additional Payments Until Transfer: ${formatCurrency(breakdown.additionalPaymentsBeforeFirst)} (${breakdown.monthsUntilFirstPayment} payments)
   • Our Cash Offer: ${formatCurrency(breakdown.cashOfferAmount)}
   
   🎯 TOTAL CASH BENEFIT: ${formatCurrency(breakdown.totalCashFlow)}

✅ WHY THIS OFFER BENEFITS YOU:
   • Immediate liquidity - no more waiting for monthly payments
   • Zero risk of buyer default or property management issues
   • Complete transaction closure with professional documentation
   • Fast, reliable closing process

📋 NEXT STEPS:
If you're interested in moving forward, I'll prepare the complete purchase agreement and handle all documentation. The formal agreement will be sent as an attachment for your review.

This offer is time-sensitive and represents excellent value given your total cash position. I'm available to discuss any questions or concerns you may have.

Best regards,
${agreementData.brokerCompanyName || 'Contract Funding Group, LLC'}

---
This communication is confidential and intended solely for the addressee.`;
                  };

                  return (
                    <div className="space-y-4">
                      <Textarea
                        value={generateOfferLetter()}
                        readOnly
                        rows={25}
                        className="font-mono text-sm bg-white border-2 border-green-300 cursor-pointer hover:border-green-400 transition-colors"
                        onClick={(e) => {
                          e.currentTarget.select();
                          navigator.clipboard.writeText(generateOfferLetter());
                          toast({
                            title: "Offer Letter Copied! 📧",
                            description: "Professional offer letter copied to clipboard - ready to paste into email!",
                            duration: 3000,
                          });
                        }}
                      />
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-2">📋 Quick Summary:</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Property:</span> {agreementData.propertyAddress || selectedDeal.property_address}
                          </div>
                          <div>
                            <span className="font-medium">Cash Offer:</span> {formatCurrency(parseFloat(agreementData.brokerOfferToSeller) || 0)}
                          </div>
                          <div>
                            <span className="font-medium">Total Benefit:</span> {formatCurrency(breakdown.totalCashFlow)}
                          </div>
                          <div>
                            <span className="font-medium">Transfer Date:</span> {agreementData.firstPaymentDate || 'TBD'}
                          </div>
                        </div>
                        <p className="text-xs text-green-600 mt-3 italic">
                          ✨ Click the text area above to copy this professional offer letter directly to your email
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 flex-wrap">
            <Button onClick={() => setShowPreview(!showPreview)} variant="outline">
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? "Hide Preview" : "Show Preview"}
            </Button>
            <Button onClick={handleSaveAgreement} variant="outline" className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Save Agreement
            </Button>
            <Button onClick={handleEmailAgreement} variant="outline" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Package
            </Button>
            <Button onClick={handleDownloadWord} variant="default" className="flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              Download Word Document
            </Button>
            <Button onClick={handlePrintPackage} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Print Package
            </Button>
            <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Print Agreement Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Section */}
      {showPreview && (
        <Card>
          <CardHeader>
            <CardTitle>Agreement Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap font-mono text-sm bg-muted p-4 rounded-lg overflow-auto">
              {agreementData.agreementType === "partial" 
                ? generatePartialAgreement() 
                : generateFullAgreement()}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Saved Agreements Section */}
      {savedAgreements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Saved Agreements</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seller Name</TableHead>
                  <TableHead>Property Address</TableHead>
                  <TableHead>Agreement Type</TableHead>
                  <TableHead>Agreement Date</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedAgreements.map((agreement) => (
                  <TableRow key={agreement.id}>
                    <TableCell>{agreement.seller_name}</TableCell>
                    <TableCell className="max-w-xs truncate">{agreement.property_address}</TableCell>
                    <TableCell className="capitalize">{agreement.agreement_type}</TableCell>
                    <TableCell>
                      {agreement.agreement_date ? format(new Date(agreement.agreement_date), "MMM d, yyyy") : "-"}
                    </TableCell>
                    <TableCell>
                      {format(new Date(agreement.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => viewSavedAgreement(agreement)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Agreement</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the agreement for "{agreement.seller_name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAgreement(agreement.id, agreement.seller_name)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete Agreement
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}